/**
 * Throws when a required runtime env var is missing — except in test
 * environments, where the service module needs to be importable
 * without crashing the test runner. Vitest sets `MODE === 'test'`
 * automatically.
 *
 * Why the `MODE !== 'test'` carve-out: services like `amplitude` and
 * `supabase` are pulled in transitively by tests via shared code
 * (e.g. `sidepanel.tsx → useCanCall → use-microphone-permission →
 * amplitude`), even though the tests never invoke the service. A
 * top-level throw at module-import time prevents those tests from
 * loading at all. We keep the fail-fast in prod / staging / dev so
 * an operator catches a missing env at boot, not mid-user-flow.
 *
 * Usage:
 *
 * ```ts
 * const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
 * requireEnv('VITE_AMPLITUDE_API_KEY', apiKey);
 * ```
 *
 * Or for multiple values that share a single error message:
 *
 * ```ts
 * requireEnv('Supabase environment variables', supabaseUrl && supabaseKey);
 * ```
 */
export const requireEnv = (name: string, value: string | undefined): void => {
  if (!value && import.meta.env.MODE !== 'test') {
    throw new Error(`Missing ${name}`);
  }
};
